import { Planet } from '@apeiron/planet'
import {
  deserializeData,
  ScreenLogType,
  useAccountContext,
  useScreenLog
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { NftType } from '@src/constants/bookmark'
import { planetSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/getPlanetDetail'
import useGetPlanetBookmarkStatus from '@src/hooks/graphql/useGetNFTBookmarkStatus'
import { useCallback, useState } from 'react'

const useGetPlanet = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const { intercept } = useScreenLog()

  const { isLoggedIn } = useAccountContext()

  const { fetch: getBookmarkStatus } = useGetPlanetBookmarkStatus()

  const [data, setData] = useState<Response>(null)

  const fetch = useCallback(
    async (request: Request) => {
      const { planetId } = request

      const response = await query({ variables: { planetID: planetId } })

      if (response.data) {
        const planet = deserializeData<Planet>(response, planetSchema)

        if (planet) {
          setData(planet)

          if (isLoggedIn) {
            const isBookmarked = await getBookmarkStatus({
              nftType: NftType.Planet,
              tokenId: planet.planetID
            })

            setData({
              ...planet,
              isBookmarked
            })
          }
        }

        return planet
      }

      return null
    },
    [getBookmarkStatus, isLoggedIn, query]
  )

  return {
    fetch: intercept({
      runJob: fetch,
      operation: 'useGetPlanet',
      type: ScreenLogType.Graphql
    }),
    data,
    loading
  }
}

type Request = {
  planetId: string
}

type Response = Planet | null

type Hook = {
  fetch: (request: Request) => Promise<Response>
  data: Response
  loading: boolean
}

export default useGetPlanet
